import React, {Component} from 'react';
import {connect} from 'react-redux';
import xssFilters from 'xss-filters';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import {withLDConsumer} from 'launchdarkly-react-client-sdk';

import I18nLoader from '../I18nLoader';
import LoadingSpinner from '../LoadingSpinner';
import UniversalHelp from '../UniversalHelp';
import {
  enabledLangsAndCountry,
  contentSuggestionLangMap,
} from '../../app.config';
import {
  memoizedParseQueryParams,
  isObjectEmpty,
} from '../../utils/utilFunctions';
import {
  updateSettingsSiteData,
  fetchProductReleases,
  updateStoreLdClient,
  updateUiData,
} from '../../actions';
import {adpAnalyticsInit} from '../../utils/utilADPAnalytics';

const log = require('loglevel');

// main app, wrapped in language provider
const loaderStyles = {
  width: '100vw',
  flexDirection: 'column',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '50vh',
};

class App extends Component {
  state = {
    isLoading: true,
  };

  async componentDidMount() {
    const {
      queryParams,
      updateSettingsSiteData,
      siteData,
      fetchProductReleases,
      workflowData,
      workflowViaProp,
      updateStoreLdClient,
      updateUiData,
      ldClient,
    } = this.props;
    this.setState({isLoading: false});
    if (isObjectEmpty(workflowData) && isObjectEmpty(workflowViaProp)) {
      /**
       * get products from beehive api.
       * refetch the products to sync up with beehive
       */
      await fetchProductReleases();
    }
    // parent page sends params
    // that is then passed to the iframe as query param
    // iframe src might have search params like ?language=de_DE&country=DE&product=ACD&version=2014
    // we also have default params coming from the sites.config file
    // we prioritize the values received from the consuming web app over the config parameters
    // we save the data  to thre redux store so it can be used in the other areas of our app

    // get options variable
    let getParams = memoizedParseQueryParams(queryParams, [
      'country',
      'product',
      'version',
      'showFullPage',
      'containerId',
      'analyticsLogging', //GDPR cookie from parent site
      'allowImproveExperience', //GDPR cookie from parent site
      'mode',
      'integrationType',
      'isMobileDevice',
      'showContextualPopUp',
      'testAutomation'
    ]);

    // country
    let enabledCountries = enabledLangsAndCountry.map((v) => v.split('_')[1]);
    if (getParams.country && includes(enabledCountries, getParams.country)) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(getParams.country);
      siteData.country = filteredVal;
    }

    siteData.testAutomation = getParams.testAutomation === 'true' ? true : false

    // product
    let filteredProductVal;
    let hasPresetProductForDropDown1;
    if (getParams.product) {
      filteredProductVal = xssFilters.uriQueryInUnQuotedAttr(getParams.product);
    } else {
      filteredProductVal = siteData.product; // If the parent site didn't pass the product, then default it to siteconfig.
    }
    if (typeof filteredProductVal === 'string' && filteredProductVal.length) {
      hasPresetProductForDropDown1 =
        this.presetProductContext(filteredProductVal);

      log.debug(
        `sending hasPresetProductForDropDown1 ${JSON.stringify(
          hasPresetProductForDropDown1
        )}`
      );

      if (!isEmpty(hasPresetProductForDropDown1)) {
        // set app product
        siteData.product = filteredProductVal;
        siteData.productDetails = hasPresetProductForDropDown1;
      }
    }

    if (isEmpty(siteData.productDetails)) {
      // update product selection for dropdown if required
      let hasPresetProductForDropDown2 = this.presetProductContext(
        siteData.product
      );
      log.debug(
        `sending hasPresetProductForDropDown2 ${JSON.stringify(
          hasPresetProductForDropDown2
        )}`
      );

      if (!isEmpty(hasPresetProductForDropDown2)) {
        siteData.productDetails = hasPresetProductForDropDown2;
      } else {
        siteData.productDetails = siteData.product
          ? {label: '', value: siteData.product}
          : {};
      }
    }
    log.debug(`sending siteData.productDetails
    filteredProductVal: ${filteredProductVal}
    ${JSON.stringify(siteData.productDetails)}`);

    // version
    if (getParams.version) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(getParams.version);
      siteData.version = filteredVal;
    }

    // showFullPage
    if (getParams.showFullPage) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.showFullPage
      );
      const showFullPage = filteredVal || filteredVal === 'true' ? true : false;
      siteData.showFullPage = showFullPage;
    }

    // mode
    if (getParams.mode) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(getParams.mode);
      const mode =
        filteredVal ||
        ['fullPage', 'widget', 'openWidget'].includes(filteredVal)
          ? filteredVal
          : 'widget';
      siteData.mode = mode;
    } else {
      siteData.mode = null;
    }

    // 'integrationType'
    delete siteData.integrationType;
    if (getParams.integrationType) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.integrationType
      );
      const integrationType = ['In-Product'].includes(filteredVal)
        ? filteredVal
        : '';
      siteData.integrationType = integrationType;
    }

    // containerId
    if (getParams.containerId) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.containerId
      );
      siteData.containerId = filteredVal;
    }

    // trigger contextual popup
    if (getParams.showContextualPopUp !== null) {
      siteData.showContextualPopUp = getParams.showContextualPopUp === 'true' ? true : false;
    }

    if (
      contentSuggestionLangMap[siteData.language] === 'ENU' &&
      siteData.language.slice(0, 2) !== 'en'
    ) {
      siteData.showLangFallbackMsg = true;
    }

    if (getParams.analyticsLogging) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.analyticsLogging
      );

      siteData.analyticsLogging = filteredVal === 'true' ? true : false;
    } else {
      siteData.analyticsLogging = false;
    }

    if (getParams.allowImproveExperience) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.allowImproveExperience
      );

      siteData.allowImproveExperience = filteredVal === 'true' ? true : false;
    } else {
      siteData.allowImproveExperience = false;
    }

    if (getParams.isMobileDevice) {
      let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
        getParams.isMobileDevice
      );

      siteData.isMobileDevice = filteredVal === 'true' ? true : false;
    } else {
      siteData.isMobileDevice = false;
    }

    // save siteData to the store
    updateSettingsSiteData({...siteData});
    siteData.integrationType &&
      updateUiData({integrationType: siteData.integrationType});
    if (ldClient) {
      updateStoreLdClient(ldClient);
    }

    // init ADP object
    adpAnalyticsInit();
  }

  // if the site provides a product context,
  // and the user has not already interaction with the product picker
  // then pre-select the sites product context in the product selector dropdown
  presetProductContext = (presetProductName) => {
    const {allProductsReleases} = this.props;
    // log.debug(`sending allProductsReleases ${JSON.stringify(allProductsReleases)}`);
    let retVal = '';
    if (!isEmpty(presetProductName)) {
      // find the product object in productReleases
      let findProd = find(allProductsReleases, ['value', presetProductName]);

      if (typeof findProd !== 'undefined' && !isEmpty(findProd)) {
        // update the value of selectedProduct
        retVal = findProd;
      }
    }
    return retVal;
  };

  render() {
    const {appSettings, siteData, flags: launchDarklyFlags} = this.props;
    const {integrationType, product} = appSettings;
    // always load UA in product for AutoCAD before LD flag is evaluated
    if (!(integrationType === 'In-Product' && product !== 'NINVFUS')) {
      if (
        isObjectEmpty(appSettings) ||
        appSettings.pageDomain !== siteData.pageDomain ||
        !launchDarklyFlags.permanentShouldShowUa
      ) {
        return null;
      }
    }
    return (
      <I18nLoader language={siteData.language} catalogs={{}}>
        {this.state.isLoading ? (
          <div style={loaderStyles}>
            <LoadingSpinner />
          </div>
        ) : (
          <div
            style={{
              display: launchDarklyFlags.permanentShouldShowUa ? '' : 'none',
            }}
          >
            <UniversalHelp />
          </div>
        )}
      </I18nLoader>
    );
  }
}

// Set default props
App.defaultProps = {
  queryParams: '',
  siteData: {},
};

// Set proptype rules
App.propTypes = {
  queryParams: PropTypes.string,
  siteData: PropTypes.object,
  workflowData: PropTypes.object,
  workflowViaProp: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    appSettings: state.appSettings,
    selectedProduct: state.uiOptions.selectedProduct,
    allProductsReleases: state.workflowPrompts.productsReleases,
    workflowData: state.workflowPrompts.workflowData,
  };
};

export default withLDConsumer()(
  connect(mapStateToProps, {
    updateSettingsSiteData,
    fetchProductReleases,
    updateStoreLdClient,
    updateUiData,
  })(App)
);
